import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Section } from '../layout';
import { Title, Header, Paragraph } from '../typography';
import { Button } from '../ui';
import { FadeIn } from '../animations';
import { useIntersectionObserver } from '../../hooks';

interface ImageTextSectionProps {
    imageSrc: string;
    imageAlt: string;
    imagePosition: 'left' | 'right';
    title: string;
    header: string;
    headerHighlight?: string;
    paragraph: string;
    buttonText?: string;
    buttonHref?: string;
    buttonOnClick?: () => void;
    backgroundColor?: string;
}

const SplitContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        position: relative;
        grid-template-columns: 1fr;
        height: auto;
        min-height: 100%;
    }
`;

const zoomOut = keyframes`
    from {
        transform: scale(1.15);
    }
    to {
        transform: scale(1);
    }
`;

const ImageSide = styled.div<{
    $imageSrc: string;
    $position: 'left' | 'right';
    $isVisible: boolean;
}>`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    order: ${({ $position }) => ($position === 'left' ? 1 : 2)};
    z-index: 20;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url(${({ $imageSrc }) => $imageSrc});
        background-size: cover;
        background-position: center;
        transform: scale(1.15);
        animation: ${({ $isVisible }) => ($isVisible ? zoomOut : 'none')} 1.2s ease-out;
        animation-fill-mode: forwards;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        order: 1;
        min-height: 50vh;
    }
`;

const TextSide = styled.div<{ $position: 'left' | 'right' }>`
    position: relative;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.backgroundDark};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing.xxl};
    order: ${({ $position }) => ($position === 'left' ? 2 : 1)};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        order: 2;
        padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
        min-height: 50vh;
        height: auto;
    }
`;

const TextContent = styled.div`
    color: ${({ theme }) => theme.colors.text};
    max-width: 600px;
    width: 100%;
`;

const ButtonWrapper = styled.div`
    margin-top: ${({ theme }) => theme.spacing.xl};
`;

export const ImageTextSection: React.FC<ImageTextSectionProps> = ({
    imageSrc,
    imageAlt,
    imagePosition,
    title,
    header,
    headerHighlight,
    paragraph,
    buttonText,
    buttonHref,
    buttonOnClick,
    backgroundColor,
}) => {
    const [imageRef, isImageVisible] = useIntersectionObserver({
        threshold: 0.2,
        triggerOnce: true,
    });

    return (
        <Section fullHeight customHeight="120vh">
            <SplitContainer>
                <ImageSide
                    ref={imageRef as React.RefObject<HTMLDivElement>}
                    $imageSrc={imageSrc}
                    $position={imagePosition}
                    $isVisible={isImageVisible}
                />
                <TextSide $position={imagePosition}>
                    <TextContent>
                        <FadeIn delay={0}>
                            <Title>{title}</Title>
                        </FadeIn>
                        <FadeIn delay={200}>
                            <Header highlightText={headerHighlight}>{header}</Header>
                        </FadeIn>
                        <FadeIn delay={400}>
                            <Paragraph>{paragraph}</Paragraph>
                        </FadeIn>
                        {buttonText && (
                            <FadeIn delay={600}>
                                <ButtonWrapper>
                                    <Button
                                        text={buttonText}
                                        href={buttonHref}
                                        onClick={buttonOnClick}
                                    />
                                </ButtonWrapper>
                            </FadeIn>
                        )}
                    </TextContent>
                </TextSide>
            </SplitContainer>
        </Section>
    );
};
